import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { connect } from 'redux-bundler-react';
import React from 'react';

import AuthorizationCheck from '../../atoms/AuthorizationCheck';
import RdpCredentialsDialog from '../workstation_detail/RdpCredentialsDialog';
import RdpDialog from '../workstation_detail/RdpDialog';
import SsmDialog from '../workstation_detail/SsmDialog';
import SsmLoadingDialog from '../workstation_detail/SsmLoadingDialog';

// Admin tool: connect to an arbitrary instance in the facility over SSM — either
// an interactive SSM shell or RDP-over-SSM (to the box-side rdp-gateway). Reuses
// the instance-connect pattern from ParsecDetails/DcvGatewayDetails (SSM) and the
// WorkstationMenu RDP flow, but takes a free-form instance id instead of a
// workstation. Both go through doStart*Session({ instanceId, facility }), which
// already runs + verifies the WebAuthn ceremony. Each connection type is gated
// by its own permission at the radio option (ssm.session.create / the RDP one).
const ConnectToInstanceDialog = ({
  open,
  onClose,
  activeFacility,
  doStartSsmSession,
  doStartRdpSession,
}) => {
  const [connectionType, setConnectionType] = React.useState('ssm');
  // The SSM Scripts tooling (Install/Execute/recipe edit) and the SSM-logs
  // shortcut are platform-gated. With no workstation to infer the OS from, the
  // admin picks it; it's threaded into SsmDialog -> Scripts. (RDP is Windows.)
  const [platform, setPlatform] = React.useState('windows');
  const [instanceId, setInstanceId] = React.useState('');

  // SSM shell
  const [ssmSession, setSsmSession] = React.useState(null);
  const [ssmLoading, setSsmLoading] = React.useState(false);

  // RDP-over-SSM
  const [showRdpCreds, setShowRdpCreds] = React.useState(false);
  const [rdpSession, setRdpSession] = React.useState(null);
  const [rdpCredentials, setRdpCredentials] = React.useState(null);
  const [openEmbeddedRdp, setOpenEmbeddedRdp] = React.useState(false);
  const [rdpConnecting, setRdpConnecting] = React.useState(false);

  const trimmedId = instanceId.trim();

  const startSsm = async () => {
    setSsmLoading(true);
    try {
      const s = await doStartSsmSession({
        instanceId: trimmedId,
        facility: activeFacility,
      });
      setSsmLoading(false);
      if (s.success) {
        setSsmSession(s);
      } else {
        alert(
          'The SSM session could not be created. The instance may not be connected.',
        );
      }
    } catch (err) {
      setSsmLoading(false);
      alert(err);
    }
  };

  const handleConnect = () => {
    if (!trimmedId) return;
    onClose();
    if (connectionType === 'rdp') {
      setShowRdpCreds(true);
    } else {
      startSsm();
    }
  };

  // RDP: collect NLA creds, then open the port-forward session and hand off to
  // RdpDialog. "Connecting" shows from creds-submit through the desktop paint.
  const handleRdpCredsSubmit = async credentials => {
    setShowRdpCreds(false);
    setRdpCredentials(credentials);
    setRdpConnecting(true);
    try {
      const s = await doStartRdpSession({
        instanceId: trimmedId,
        facility: activeFacility,
      });
      if (s.success) {
        setRdpSession(s);
        setOpenEmbeddedRdp(true);
      } else {
        setRdpConnecting(false);
        alert('RDP session could not be established');
      }
    } catch (err) {
      setRdpConnecting(false);
      alert(err);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Connect to instance</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
        >
          <FormControl>
            <FormLabel>Connection</FormLabel>
            <RadioGroup
              value={connectionType}
              onChange={e => setConnectionType(e.target.value)}
            >
              <AuthorizationCheck permissionName="ssm.session.create">
                <FormControlLabel
                  value="ssm"
                  control={<Radio />}
                  label="SSM (shell)"
                />
              </AuthorizationCheck>
              <AuthorizationCheck permissionName="workstation.rdp.connect">
                <FormControlLabel
                  value="rdp"
                  control={<Radio />}
                  label="RDP over SSM"
                />
              </AuthorizationCheck>
            </RadioGroup>
          </FormControl>
          {connectionType === 'ssm' && (
            <FormControl>
              <FormLabel>Platform</FormLabel>
              <RadioGroup
                row
                value={platform}
                onChange={e => setPlatform(e.target.value)}
              >
                <FormControlLabel
                  value="windows"
                  control={<Radio />}
                  label="Windows"
                />
                <FormControlLabel
                  value="linux"
                  control={<Radio />}
                  label="Linux"
                />
              </RadioGroup>
            </FormControl>
          )}
          <TextField
            label="Instance ID"
            placeholder="i-0123456789abcdef0"
            value={instanceId}
            onChange={e => setInstanceId(e.target.value)}
            autoFocus
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleConnect}
            variant="contained"
            disabled={!trimmedId}
          >
            Connect
          </Button>
        </DialogActions>
      </Dialog>

      {/* SSM shell */}
      <SsmLoadingDialog session={ssmSession} open={ssmLoading} />
      <SsmDialog
        session={ssmSession}
        open={Boolean(ssmSession)}
        onClose={() => setSsmSession(null)}
        instanceId={trimmedId}
        name={`Instance ${trimmedId}`}
        platform={platform}
      />

      {/* RDP-over-SSM */}
      <RdpCredentialsDialog
        open={showRdpCreds}
        onCancel={() => setShowRdpCreds(false)}
        onSubmit={handleRdpCredsSubmit}
      />
      {openEmbeddedRdp && (
        <RdpDialog
          open
          session={rdpSession}
          credentials={rdpCredentials}
          onConnected={() => setRdpConnecting(false)}
          onClose={() => {
            setOpenEmbeddedRdp(false);
            setRdpConnecting(false);
          }}
        />
      )}
      {/* "Connecting" from creds-submit until the desktop paints; high z-index
          so it sits above the full-screen RdpDialog. */}
      <Dialog open={rdpConnecting} maxWidth="sm" fullWidth sx={{ zIndex: 4000 }}>
        <DialogTitle>Connecting</DialogTitle>
        <DialogContent>
          <LinearProgress />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default connect(
  'selectActiveFacility',
  'doStartSsmSession',
  'doStartRdpSession',
  ConnectToInstanceDialog,
);
